import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { AuthService } from './services/auth.service';
import { ProductService } from './services/product.service';
import { CartService } from './services/cart.service';
import { WishlistService } from './services/wishlist.service';
import { AnalyticsService } from './services/analytics.service';
import { ToastService } from './services/toast.service';
import { NewsletterService } from './services/newsletter.service';
import { FormsModule } from '@angular/forms';
import { Observable, combineLatest, map, switchMap, take, of } from 'rxjs';
import { Product } from './models/admin.models';

import { NavbarComponent } from './shared/navbar/navbar.component';
import { AuthModalService } from './shared/auth-modal/auth-modal.service';

import { LazyImageComponent } from './shared/components/lazy-image/lazy-image.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterLink, NavbarComponent, FormsModule, LazyImageComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  private authService = inject(AuthService);
  private productService = inject(ProductService);
  private cartService = inject(CartService);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private analyticsService = inject(AnalyticsService);
  protected wishlistService = inject(WishlistService);
  private newsletterService = inject(NewsletterService);
  private authModalService = inject(AuthModalService);

  selectedCategory = '';
  searchQuery = '';
  email = '';
  
  userProfile$ = this.authService.userProfile$;
  products$: Observable<Product[]> = this.route.queryParams.pipe(
    switchMap(params => {
      this.searchQuery = params['q'] || '';
      this.selectedCategory = params['category'] || '';
      return this.productService.getProducts(this.searchQuery, this.selectedCategory).pipe(
        map(products => products.sort((a, b) => this.getDiscountedPrice(a) - this.getDiscountedPrice(b)))
      );
    })
  );
  cartItemCount$ = this.cartService.getCartItemCount();

  ngOnInit() {
    this.authService.refreshUserProfile();
    this.setMetaTags();
    this.analyticsService.logPageView('/home');
  }

  scrollToProducts() {
    const el = document.getElementById('products-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  setMetaTags() {
    this.titleService.setTitle('Aarvya Botanicals | Ancient Wisdom. Modern Rituals.');
    this.metaService.updateTag({ name: 'description', content: 'Discover authentic Ayurvedic rituals and botanical treasures for skincare, wellness, and body sanctuary.' });
    this.metaService.updateTag({ property: 'og:title', content: 'Aarvya Botanicals' });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ name: 'keywords', content: 'ayurveda, botanical, skincare, wellness, natural beauty, india' });
  }

  getImageUrl(image: string): string {
    if (!image) return '/assets/placeholder-botanical.jpg';
    return image.startsWith('http') ? image : '/' + image;
  }

  getDiscountedPrice(product: Product): number {
    if (!product.discount) return product.price;
    return product.price * (1 - product.discount / 100);
  }

  addToCart(product: Product, event: Event) {
    event.stopPropagation(); // Prevent navigation to product details
    if (!product.isInStock) return;
    
    this.cartService.addToCart(product, 1);
    this.analyticsService.trackAddToCart(product, 1);
    this.toastService.success(`${product.name} added to your ritual!`);
  }

  toggleWishlist(product: Product, event: Event) {
    event.stopPropagation();
    
    // Check if user is logged in
    const user = this.authService.currentUserValue;
    if (!user) {
      this.authModalService.openSignIn();
      return;
    }
    
    this.authService.userProfile$.pipe(
      take(1),
      map(user => user?.uid),
      switchMap(uid => {
        if (!uid) {
           this.toastService.error('Please sign in to add to wishlist');
           return of(null);
        }
        if (this.wishlistService.isInWishlist(product.id)) {
          this.wishlistService.removeFromWishlist(uid, product.id);
          this.toastService.success('Removed from wishlist');
        } else {
          this.wishlistService.addToWishlist(uid, product);
          this.toastService.success('Added to wishlist');
        }
        return of(true);
      })
    ).subscribe();
  }




  subscribe() {
    if (!this.email) return;
    this.newsletterService.subscribe(this.email).subscribe({
      next: () => {
        this.toastService.success('Subscribed successfully!');
        this.email = '';
      },
      error: () => this.toastService.error('Failed to subscribe. Please try again.')
    });
  }
}
